import React, { useRef, useEffect, useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useDiary, ACTIONS } from './DiaryContext';
import { DragIcon, ExpandICon } from '../SvgIcon';
import NotificationBell from '../notifications/NotificationBell';
import LocationPicker from '../multiLocation/LocationPicker';

/**
 * StaffFilter - Reuses V1's exact filter design (class names + structure).
 * Desktop: visible-staff-body dropdown with drag-show_body toggle + react-beautiful-dnd.
 * Mobile: filter-sheet bottom sheet with @dnd-kit.
 */
function StaffFilter({ open, onToggle, onReorder }) {
  const { state, dispatch } = useDiary();
  const { staffOrder, staffById, filterMode, filteredStaffIds } = state;
  const filterRef = useRef(null);

  const allLanes = staffOrder.map(id => staffById[id]).filter(Boolean);
  // Rooms are lanes, not people: they get their own section below and stay
  // out of the drag lists so reordering never persists a res_* id.
  const allStaff = allLanes.filter(s => !s.isResource);
  const resourceLanes = allLanes.filter(s => s.isResource);

  const isAllSelected = filterMode === 'all';
  const isWorkingSelected = filterMode === 'working';

  const isStaffVisible = (staffId) => {
    if (filterMode === 'all') return true;
    if (filterMode === 'working') return staffById[staffId]?.isWorking;
    return filteredStaffIds.includes(staffId);
  };

  const handlePreset = (e) => {
    const name = e.target.name;
    if (name === 'all_staff') {
      dispatch({ type: ACTIONS.SET_FILTER, payload: { mode: 'all', staffIds: [] } });
    } else if (name === 'working_staff') {
      dispatch({ type: ACTIONS.SET_FILTER, payload: { mode: 'working', staffIds: [] } });
    }
  };

  const handleToggleStaff = (e, staffId) => {
    let currentIds;
    if (filterMode === 'custom') {
      currentIds = [...filteredStaffIds];
    } else if (filterMode === 'working') {
      currentIds = allStaff.filter(s => s.isWorking).map(s => s.id);
    } else {
      currentIds = staffOrder.slice();
    }

    const idx = currentIds.indexOf(staffId);
    if (idx >= 0) {
      currentIds.splice(idx, 1);
    } else {
      currentIds.push(staffId);
    }

    if (currentIds.length === 0 || currentIds.length === staffOrder.length) {
      dispatch({ type: ACTIONS.SET_FILTER, payload: { mode: 'all', staffIds: [] } });
    } else {
      dispatch({ type: ACTIONS.SET_FILTER, payload: { mode: 'custom', staffIds: currentIds } });
    }
  };

  // Desktop drag end (react-beautiful-dnd)
  const handleDesktopDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const newOrder = Array.from(staffOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);

    dispatch({ type: ACTIONS.SET_STAFF_ORDER, payload: newOrder });

    if (onReorder) {
      const reorderedRaw = newOrder.map(id => staffById[id]?.raw).filter(Boolean);
      onReorder(reorderedRaw);
    }
  }, [staffOrder, staffById, dispatch, onReorder]);

  // Mobile drag end (@dnd-kit)
  const handleMobileDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = staffOrder.indexOf(active.id);
    const newIndex = staffOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(staffOrder, oldIndex, newIndex);
    dispatch({ type: ACTIONS.SET_STAFF_ORDER, payload: newOrder });

    if (onReorder) {
      const reorderedRaw = newOrder.map(id => staffById[id]?.raw).filter(Boolean);
      onReorder(reorderedRaw);
    }
  }, [staffOrder, staffById, dispatch, onReorder]);

  let filterLabel = 'All Staff';
  if (filterMode === 'working') filterLabel = 'Working Staff';
  else if (filterMode === 'custom') {
    const count = filteredStaffIds.length;
    if (count === 1) {
      const s = staffById[filteredStaffIds[0]];
      filterLabel = s ? s.name : `${count} Staff`;
    } else {
      filterLabel = `${count} Staff`;
    }
  }

  const mobileSheetRef = useRef(null);

  // Close on outside click (desktop dropdown + mobile sheet).
  // Must check BOTH refs because on mobile the desktop dropdown is hidden
  // via CSS but still in the DOM, so clicks inside the mobile sheet would
  // otherwise be treated as "outside" and close the filter immediately.
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      const inDesktop = filterRef.current && filterRef.current.contains(e.target);
      const inMobile = mobileSheetRef.current && mobileSheetRef.current.contains(e.target);
      if (!inDesktop && !inMobile) {
        onToggle();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onToggle]);

  return (
    <>
      {/* Desktop filter */}
      <div className={`visible-staff-body d-none d-md-block ${open ? 'desktop-filter-open' : ''}`}>
        {/* Setup progress and posts ready to share live in the bell now
            (its ring and its To do), not in the diary header. */}
        <div>
          {open && <div className="client_filter_up"></div>}
          <div className="dv2-filter-row">
          <div className={`client_filter ${open ? 'open' : ''}`}>
            <div className="drag-show_body" onClick={onToggle}>
              <label>{filterLabel}</label>
              <span className={open ? 'right_corner _filter_rotate' : 'right_corner'}><ExpandICon /></span>
            </div>
            {open && (
              <DragDropContext onDragEnd={handleDesktopDragEnd}>
                <Droppable droppableId="dv2-staff-filter">
                  {(provided) => (
                    <div className="drag_dropdown_body" ref={(el) => { filterRef.current = el; provided.innerRef(el); }} {...provided.droppableProps}>
                      <ul>
                        <li className="desktop-filter-preset">
                          <label className="desktop-filter-label">
                            <input type="checkbox" name="all_staff" checked={isAllSelected} onChange={handlePreset} />
                            <span className="desktop-filter-check">
                              <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </span>
                            <span>All Staff</span>
                          </label>
                        </li>
                        <li className="desktop-filter-preset">
                          <label className="desktop-filter-label">
                            <input type="checkbox" name="working_staff" checked={isWorkingSelected} onChange={handlePreset} />
                            <span className="desktop-filter-check">
                              <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </span>
                            <span>Working Staff</span>
                          </label>
                        </li>
                        {allStaff.map((s, index) => (
                          <Draggable key={s.id} draggableId={s.id} index={index}>
                            {(provided, snapshot) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={snapshot.isDragging ? 'dv2-filter-dragging' : ''}
                              >
                                <label className="desktop-filter-label">
                                  <input type="checkbox" name={s.id} checked={isStaffVisible(s.id)} onChange={(e) => handleToggleStaff(e, s.id)} />
                                  <span className="desktop-filter-check">
                                    <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  </span>
                                  <span>{s.name}</span>
                                </label>
                                <span {...provided.dragHandleProps}><DragIcon /></span>
                              </li>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {resourceLanes.length > 0 && (
                          <>
                            <li className="dv2-filter-section-label">Rooms &amp; equipment</li>
                            {resourceLanes.map(s => (
                              <li key={s.id}>
                                <label className="desktop-filter-label">
                                  <input type="checkbox" name={s.id} checked={isStaffVisible(s.id)} onChange={(e) => handleToggleStaff(e, s.id)} />
                                  <span className="desktop-filter-check">
                                    <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                  </span>
                                  <span className="dv2-filter-res"><span className="dv2-filter-res-dot" style={{ background: s.diaryColour }} />{s.name}</span>
                                </label>
                              </li>
                            ))}
                          </>
                        )}
                      </ul>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
          </div>
        </div>
        <LocationPicker />
        <NotificationBell />
      </div>

      {/* Mobile filter bottom sheet */}
      {open && window.innerWidth < 768 && (
        <div className="filter-sheet-overlay" onClick={e => { if (e.target === e.currentTarget) onToggle(); }}>
          <div className="filter-sheet" ref={mobileSheetRef} onClick={e => e.stopPropagation()}>
            <div className="filter-sheet-header">
              <div className="filter-sheet-header-spacer" />
              <h2 className="filter-sheet-title">Filter Staff</h2>
              <button className="filter-sheet-close-btn" onClick={onToggle} aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="filter-sheet-body">
              <ul className="filter-sheet-list">
                <li className="filter-sheet-item filter-sheet-item--preset">
                  <div
                    className="filter-sheet-label"
                    role="checkbox"
                    aria-checked={isAllSelected}
                    tabIndex={0}
                    onClick={() => handlePreset({ target: { name: 'all_staff' } })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePreset({ target: { name: 'all_staff' } }); } }}
                  >
                    <span className={`filter-sheet-check ${isAllSelected ? 'is-checked' : ''}`}>
                      <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span>All Staff</span>
                  </div>
                </li>
                <li className="filter-sheet-item filter-sheet-item--preset">
                  <div
                    className="filter-sheet-label"
                    role="checkbox"
                    aria-checked={isWorkingSelected}
                    tabIndex={0}
                    onClick={() => handlePreset({ target: { name: 'working_staff' } })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePreset({ target: { name: 'working_staff' } }); } }}
                  >
                    <span className={`filter-sheet-check ${isWorkingSelected ? 'is-checked' : ''}`}>
                      <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span>Working Staff</span>
                  </div>
                </li>
              </ul>
              <MobileDndList
                staffOrder={staffOrder}
                staffById={staffById}
                isStaffVisible={isStaffVisible}
                onToggleStaff={handleToggleStaff}
                onDragEnd={handleMobileDragEnd}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Mobile sortable list using @dnd-kit
 */
function MobileDndList({ staffOrder, staffById, isStaffVisible, onToggleStaff, onDragEnd }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const allLanes = staffOrder.map(id => staffById[id]).filter(Boolean);
  // Rooms stay out of the sortable list (their order never persists) and get
  // a static section beneath, same as desktop.
  const allStaff = allLanes.filter(st => !st.isResource);
  const resourceLanes = allLanes.filter(st => st.isResource);
  const humanOrder = staffOrder.filter(id => !(staffById[id] && staffById[id].isResource));

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      modifiers={[restrictToVerticalAxis]}
      sensors={sensors}
    >
      <SortableContext items={humanOrder} strategy={verticalListSortingStrategy}>
        <ul className="filter-sheet-list">
          {allStaff.map(s => (
            <SortableStaffItem
              key={s.id}
              staff={s}
              checked={isStaffVisible(s.id)}
              onToggle={onToggleStaff}
            />
          ))}
          {resourceLanes.length > 0 && (
            <>
              <li className="dv2-filter-section-label">Rooms &amp; equipment</li>
              {resourceLanes.map(s => (
                <li key={s.id} className="filter-sheet-item">
                  <div className="filter-sheet-label" role="checkbox" aria-checked={isStaffVisible(s.id)} tabIndex={0}
                    onClick={() => onToggleStaff({ target: { name: s.id } }, s.id)}>
                    <span className={`filter-sheet-check${isStaffVisible(s.id) ? ' checked' : ''}`}>
                      <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <span className="dv2-filter-res"><span className="dv2-filter-res-dot" style={{ background: s.diaryColour }} />{s.name}</span>
                  </div>
                </li>
              ))}
            </>
          )}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

/**
 * Individual sortable staff item for mobile
 */
function SortableStaffItem({ staff, checked, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: staff.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <li ref={setNodeRef} style={style} className="filter-sheet-item" {...attributes}>
      <div
        className="filter-sheet-label"
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onClick={(e) => onToggle(e, staff.id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(e, staff.id); } }}
      >
        <span className={`filter-sheet-check ${checked ? 'is-checked' : ''}`}>
          <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </span>
        <span>{staff.name}</span>
      </div>
      <span className="filter-sheet-drag" {...listeners}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 5H12M4 8H12M4 11H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </span>
    </li>
  );
}

export default StaffFilter;
